import { SetStateAction, useContext, useState } from "react";
import { defaultRetssagsProeveColumns } from "../../../../../config/person";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import IngenRegistreringer from "../IngenRegistreringer";
import SendEmailModal from "../../../../sendEmail/SendEmailModal";
import { ModalTrigger } from "../../../../Modal/Modal";
import {
  FmuretssagsproeveResultatId,
  PersonDto,
} from "../../../../../interfaces/schema";
import { SortBy } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import useActiveColumns from "../../../../../hooks/useActiveColumns";
import { RetssagsProeveField } from "../../../../../interfaces/personer";
import OpretNyButtonBlock from "../OpretNyButtonBlock";
import { retssagsProeverFormConfig } from "../../../../../config/form/person/kursus/personRetssagsProeverFormConfig";
import ModalCustom from "../../../../Modal/modals/ModalCustom";
import { UserQueryContext } from "../../../../../context/UserContext";
import { Report, useReports } from "../../../../../hooks/useQueries";
import {
  DropdownItem,
  DropdownItemIcon,
  DropdownSeparator,
} from "../../../../dropdown/Dropdown";
import ReportIcon from "../../../../icons/ReportIcon";
import { ReportLink } from "@/components/ReportLink";
import EditIcon from "../../../../icons/EditIcon";
import EmailIcon from "../../../../icons/EmailIcon";
import { useConfirm } from "../../../../confirm/useConfirm";
import { post } from "../../../../../api/util";
import { orderBy } from "../../../../../util/orderBy";

export default function RetssagsProever({
  person,
  invalidateData,
}: {
  person: PersonDto;
  invalidateData: InvalidateDataFunction<any>;
}) {
  const [actionId, setActionId] = useState<string | undefined>(undefined);
  const { confirmDialog } = useConfirm();
  const { user, hasPermission } = useContext(UserQueryContext);
  const isAuth = hasPermission(user, "KursusWrite");
  const [fmuretssagsproeveColumns, setFmuretssagsproeveActiveColumns] =
    useActiveColumns(
      "person-personretssagsproever-active-columns",
      defaultRetssagsProeveColumns,
    );
  const [clear, setClear] = useState(false);
  const [activeModal, setActiveModal] = useState<ActiveModalTypes>("sendemail");
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personRetssagsProeverSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );
  const { fmuretssagsproeveEksaminand, kundeNr } = person;
  const numberOfFailedAttempts =
    fmuretssagsproeveEksaminand?.filter(
      (r) => r.status === FmuretssagsproeveResultatId.IkkeBestaaet,
    ).length ?? 0;

  // hookys
  const { reports: allReports } = useReports();
  const reports = allReports?.filter(
    (r) => r.uiPath === "aika/web/retssagsproeve",
  );
  reports?.sort((a, b) => a.title.localeCompare(b.title));

  function downloadHandler() {
    tableToXLSX(fmuretssagsproeveColumns, [], {
      fileName: `retssagsprøver-for-${kundeNr}`,
      additionalData: {
        kundenr: kundeNr,
      },
    });
  }

  if (
    !fmuretssagsproeveEksaminand ||
    fmuretssagsproeveEksaminand.length === 0
  ) {
    return (
      <IngenRegistreringer
        invalidateData={invalidateData}
        editConfig={isAuth ? retssagsProeverFormConfig(person.id) : undefined}
      />
    );
  }

  return (
    <>
      <ButtonDownload onClick={downloadHandler} />
      <ModalProvider>
        <TableRoot>
          <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
            {getHeaderActiveColumns(fmuretssagsproeveColumns, {
              defaultSortBy,
              setSortBy,
              sortBy,
            })}
          </TableHead>
          <TableBody>
            <ModalProvider>
              {orderBy(
                fmuretssagsproeveEksaminand,
                sortBy.map((s) => s.field),
                sortBy.map((s) => s.direction),
              ).map((rp, i) => {
                return (
                  <TableRow
                    key={i}
                    moreActions={moreActions(
                      invalidateData,
                      confirmDialog,
                      setClear,
                      setActiveModal,
                      rp.id,
                      rp.fakturaStatus,
                      isAuth,
                      reports,
                    )}
                    invalidateData={invalidateData}
                    action={() => setActionId(rp.id)}
                  >
                    {fmuretssagsproeveColumns
                      .filter((col) => col.active)
                      .map((col) => {
                        return <TD key={col.field} col={col} rowData={rp} />;
                      })}
                  </TableRow>
                );
              })}

              {activeModal === "sendemail" && actionId && (
                <SendEmailModal
                  type="fmuretssagsproeve"
                  contextData={{
                    selectedIds: [actionId],
                    totalCount: 1,
                  }}
                  clear={clear}
                  setClear={setClear}
                />
              )}
              {activeModal === "edit" && actionId && (
                <ModalCustom
                  config={retssagsProeverFormConfig(person.id, actionId)}
                  title="Rediger retssagsprøve"
                  method="patch"
                  invalidateData={invalidateData}
                />
              )}
            </ModalProvider>
          </TableBody>
        </TableRoot>
        <ColumnEditorModal
          defaultColumns={defaultRetssagsProeveColumns}
          columns={fmuretssagsproeveColumns}
          setActiveColumns={setFmuretssagsproeveActiveColumns}
        />
        <OpretNyButtonBlock
          invalidateData={invalidateData}
          editConfig={
            isAuth
              ? retssagsProeverFormConfig(
                  person.id,
                  undefined,
                  numberOfFailedAttempts,
                )
              : undefined
          }
        />
        <div className="py2-">&nbsp;</div>
      </ModalProvider>
    </>
  );
}

const moreActions = (
  refetch: InvalidateDataFunction<any>,
  confirmDialog: any,
  setClear: (gate: boolean) => any,
  setActiveModal: (value: SetStateAction<ActiveModalTypes>) => any,
  retsagsproeveId: string,
  RPFakturaStatus: string,
  isAuth?: boolean,
  reports?: Array<Report>,
) => {
  const handleInvoice = async () => {
    try {
      const confirm = await confirmDialog(
        `Du har valgt at sende en faktura til retssagsprøven. Vil du fortsætte?`,
        "",
        "Afbryd",
        "Send",
      );
      if (confirm) {
        await post({
          query: `fmuretssagsproeve/${retsagsproeveId}/wizards/fakturer`,
        });
        await refetch();
      }
    } catch (error) {
      console.error(error);
    }
  };
  const edit = (
    <DropdownItem asChild key="edit">
      <ModalTrigger
        onClick={() => {
          setActiveModal("edit");
        }}
      >
        <DropdownItemIcon>
          <EditIcon />
        </DropdownItemIcon>
        Rediger retssagsprøve
      </ModalTrigger>
    </DropdownItem>
  );
  const array = [
    <DropdownItem asChild key="sendemail">
      <ModalTrigger
        onClick={() => {
          setClear(true);
          setActiveModal("sendemail");
        }}
      >
        <DropdownItemIcon>
          <EmailIcon />
        </DropdownItemIcon>
        Send email...
      </ModalTrigger>
    </DropdownItem>,
    <DropdownItem
      key={"sendfaktura"}
      onClick={handleInvoice}
      disabled={
        RPFakturaStatus !== "INGEN_STATUS" && RPFakturaStatus !== "FEJLET"
      }
    >
      <DropdownItemIcon>
        <EmailIcon />
      </DropdownItemIcon>
      Send faktura ...
    </DropdownItem>,
    <DropdownItem key={"brevflet"}>
      <DropdownItemIcon>
        <EmailIcon />
      </DropdownItemIcon>
      Brevflet eksamensbevis ...
    </DropdownItem>,
  ];
  if (isAuth) {
    array.unshift(edit);
  }

  if (reports?.length && retsagsproeveId) {
    array.push(<DropdownSeparator key={"retspr-sep"} />);
    array.push(
      ...reports.map((r) => {
        return (
          <DropdownItem asChild key={r.id}>
            <ReportLink
              url={`${r.url}?id=${retsagsproeveId}`}
              className="c-dropdown-default-item "
            >
              <DropdownItemIcon>
                <ReportIcon />
              </DropdownItemIcon>
              <span>{r.title}</span>
            </ReportLink>
          </DropdownItem>
        );
      }),
    );
  }

  return array;
};

const defaultSortBy: SortBy<RetssagsProeveField>[] = [
  {
    direction: "desc",
    field: "proceduredato",
  },
];
